import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomSidekickForm from '../components/CustomForm';
import { Card, TextInput, Button, Modal } from 'flowbite-react';
import { FaTrash } from 'react-icons/fa';
import { getAllRoles, formatRoleKey } from '../ai_config/roleUtils';

// Default roles configuration
const defaultRoles = [
  {
    title: 'Visual Designer',
    description: 'Creates social media graphics, presentations, and basic design assets.',
    icon: '🎨'
  },
  {
    title: 'Business Analyst',
    description: 'Creates reports and analyzes business metrics.',
    icon: '📊'
  },
  {
    title: 'Content Writer',
    description: 'Creates engaging blog, articles, and social media content.',
    icon: '✍️'
  },
  {
    title: 'Virtual Assistant',
    description: 'Manages schedules, emails, and administrative tasks.',
    icon: '📅'
  },
  {
    title: 'Paid Ads Manager',
    description: 'Manages paid campaigns and optimizes ad performance.',
    icon: '💰'
  },
  {
    title: 'Data Analyst',
    description: 'Processes and analyzes data for insights.',
    icon: '📈'
  },
];

const Feature = () => {
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [customRoles, setCustomRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load roles on mount
  useEffect(() => {
    loadRoles();
  }, []);

  // Load roles from localStorage and combine with default roles
  const loadRoles = () => {
    try {
      const savedCustomRoles = localStorage.getItem('customSidekicks');
      const parsedCustomRoles = savedCustomRoles ? JSON.parse(savedCustomRoles) : [];
      setCustomRoles(parsedCustomRoles);
      setAllRoles([...defaultRoles, ...parsedCustomRoles]);
    } catch (error) {
      console.error('Error loading roles:', error);
      setAllRoles(defaultRoles);
    }
  };

  // Handle custom sidekick creation
  const handleCustomSidekickSubmit = (newSidekick) => {
    const formattedSidekick = {
      ...newSidekick,
      icon: newSidekick.icon || '⚙️', // Default icon for custom sidekicks
      color: newSidekick.color || 'custom',
      actions: newSidekick.customPrompt ? [newSidekick.customPrompt] : ['Get Started']
    };

    const updatedCustomRoles = [...customRoles, formattedSidekick];
    
    // Update state and localStorage
    setCustomRoles(updatedCustomRoles);
    setAllRoles([...defaultRoles, ...updatedCustomRoles]);
    localStorage.setItem('customSidekicks', JSON.stringify(updatedCustomRoles));
    
    // Close the form
    setIsFormOpen(false);
  };

  // Handle role selection
  const handleRoleSelect = (roleTitle) => {
    const roleKey = formatRoleKey(roleTitle);
    navigate('/mysidekick', { state: { selectedRole: roleKey } });
  };

  // Open deletion confirmation modal
  const handleRoleDeleteClick = (roleTitle) => {
    // Only allow deletion of custom roles
    const isCustomRole = customRoles.some(role => role.title === roleTitle);
    if (isCustomRole) {
      setRoleToDelete(roleTitle);
      setIsModalOpen(true);
    }
  };

  // Handle role deletion
  const handleRoleDelete = () => {
    const updatedCustomRoles = customRoles.filter(role => role.title !== roleToDelete);
    
    // Update state and localStorage
    setCustomRoles(updatedCustomRoles);
    setAllRoles([...defaultRoles, ...updatedCustomRoles]);
    localStorage.setItem('customSidekicks', JSON.stringify(updatedCustomRoles));
    
    // Close modal and clear selection
    setIsModalOpen(false);
    setRoleToDelete(null);
  };

  // Filter roles based on search term
  const filteredRoles = allRoles.filter(role =>
    role.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 -mx-10 md:-mx-20 px-4 py-16">
      <div className="max-w-6xl mx-auto px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="mb-4">
            <img 
              src="/image/bot-chatting.png"
              alt="Sidekick Icon"
              className="mx-auto w-20 h-20"
            />
          </div>
          <h1 className="text-4xl font-bold mb-2">
            Choose Your Perfect <span className="text-pink-500">Sidekick</span>
          </h1>
          <p className="text-gray-600 mb-8">
            Select from our pre-trained specialist roles or create your own custom Sidekick. Each role comes with
            specialized knowledge and capabilities tailored to your needs.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-12">
            <TextInput
              placeholder="Search for a role"
              sizing="lg"
              className="rounded-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Role Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredRoles.map((role) => {
            const isCustomRole = customRoles.some(custom => custom.title === role.title);
            
            return (
              <Card key={role.title} className="hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <span className="text-2xl">{role.icon}</span>
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                      {role.title}
                    </h3>
                    <p className="font-normal text-gray-700 dark:text-gray-400 mb-4">
                      {role.description}
                    </p>
                    <Button 
                      color="pink"
                      size="sm"
                      className="text-neoPink mr-2"
                      onClick={() => handleRoleSelect(role.title)}
                    >
                      Select role →
                    </Button>
                  </div>
                  {isCustomRole && (
                    <button 
                      onClick={() => handleRoleDeleteClick(role.title)}
                      aria-label={`Delete ${role.title}`}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <FaTrash size={14} />
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Custom Sidekick Card */}
        <Card className="bg-gradient-to-r from-purple-500 to-blue-500">
          <div className="text-center text-white">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full mx-auto flex items-center justify-center mb-4">
              <div className="w-6 h-6 flex items-center justify-center">
                ⚙️
              </div>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-white">Make Your Own Sidekick</h2>
            <p className="mb-6 opacity-90">
              Don't see the perfect role? Create a custom Sidekick tailored exactly to your needs.
              Train it with your specific requirements, processes, and preferences.
            </p>
            <div className="flex justify-center">
              <Button 
                gradientDuoTone="purpleToBlue"
                size="lg"
                className="bg-white text-purple-700 hover:bg-gray-100 p-2"
                onClick={() => setIsFormOpen(true)}
              >
                Create Custom Sidekick +
              </Button>
            </div>
          </div>
        </Card>

        {/* Custom Sidekick Form */}
        <CustomSidekickForm 
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleCustomSidekickSubmit}
        />

        {/* Deletion Confirmation Modal */}
        <Modal show={isModalOpen} onClose={() => {
          setIsModalOpen(false);
          setRoleToDelete(null);
        }}>
          <Modal.Header>Confirm Deletion</Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete the "{roleToDelete}" role? This action cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button color="gray" onClick={() => {
              setIsModalOpen(false);
              setRoleToDelete(null);
            }}>
              Cancel
            </Button>
            <Button color="red" onClick={handleRoleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default Feature;